import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { cigarApi } from './cigarApi'

export interface CollectionImportPreviewRow {
  rowNumber: number
  brand: string
  cigarLine: string
  vitola: string
  quantity: number
  valid: boolean
  issues: string[]
  duplicateOfExisting: boolean
}

export interface CollectionImportPreview {
  totalRows: number
  validRows: number
  invalidRows: number
  duplicateRows: number
  rows: CollectionImportPreviewRow[]
  headerErrors: string[]
}

export interface CollectionImportResult {
  imported: number
  merged: number
  skipped: number
  failed: number
  errors: Array<{ rowNumber: number; message: string; field?: string }>
}

export const importApi = createApi({
  reducerPath: 'importApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  tagTypes: ['Import'],
  endpoints: (builder) => ({
    previewCollectionImport: builder.mutation<{ preview: CollectionImportPreview }, { csv: string }>({
      query: (body) => ({
        url: '/import/collection/preview',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { preview: CollectionImportPreview } }) => response.data,
    }),
    importCollection: builder.mutation<
      { result: CollectionImportResult },
      { csv: string; duplicateStrategy?: 'merge' | 'skip'; skipInvalid?: boolean }
    >({
      query: (body) => ({
        url: '/import/collection',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: { result: CollectionImportResult } }) => response.data,
      invalidatesTags: ['Import'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(cigarApi.util.invalidateTags(['Cigar']))
        } catch {
          // handled by caller
        }
      },
    }),
  }),
})

export const { usePreviewCollectionImportMutation, useImportCollectionMutation } = importApi
